<template>
  <div class="checkout__address-autocomplete">
    <div class="field__groups">
      <div class="field__group">
        <ValidationProvider
          class="checkout__field postcode"
          v-slot="{ errors, classes }"
          :rules="{ required: true, regex: /^[1-9][0-9]{3} ?(?!sa|sd|ss)[a-z]{2}$/i }"
          :skip-if-empty="false"
          tag="div"
          :name="$t('Postal code')"
        >
          <label>{{ $t('Postal code') }} *</label>
          <input
            type="text"
            :placeholder="$t('Postal code')"
            maxlength="6"
            :name="`${type}-address[postcode]`"
            v-model.trim="autoComplete['postcode']"
            :class="classes"
            @change="onLookupFieldsChange()"
          />
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
        <ValidationProvider
          class="checkout__field street_1"
          v-slot="{ errors, classes }"
          rules="required|integer"
          :skip-if-empty="false"
          tag="div"
          :name="$t('House number')"
        >
          <label>{{ $t('House number') }} *</label>
          <input
            :placeholder="$t('House no.')"
            type="number"
            :name="`${type}-address[street][1]`"
            v-model.trim="autoComplete['houseNr']"
            :class="classes"
            @change="onLookupFieldsChange()"
          />
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
        <ValidationProvider
          class="checkout__field street_2"
          v-slot="{ errors, classes }"
          :skip-if-empty="false"
          tag="div"
          :name="$t('Addition')"
          v-if="resolveManually || (!resolveManually && availableHouseNrAdditions.length <= 1)"
        >
          <label>{{ $t('Addition') }}</label>
          <input
            type="text"
            :placeholder="$t('Add.')"
            :name="`${type}-address[street][2]`"
            v-model.trim="autoComplete['houseNrAdd']"
            :class="classes"
            maxlength="10"
            @change="changeHouseNrAddition()"
          />
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
        <ValidationProvider
          class="checkout__field street_2"
          v-slot="{ errors, classes }"
          :skip-if-empty="false"
          tag="div"
          :name="$t('Addition')"
          v-show="!resolveManually && availableHouseNrAdditions.length > 1"
        >
          <label>{{ $t('Addition') }}</label>
          <select
            :name="`${type}-address[street][2]`"
            v-model.trim="autoComplete['houseNrAdd']"
            :class="classes"
            :disabled="!availableHouseNrAdditions.length"
            @change="changeHouseNrAddition()"
          >
            <option :value="value" v-text="value" v-for="value in availableHouseNrAdditions" :key="value" />
          </select>
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>
    </div>

    <span class="--loading --small" v-if="isLoading">
      <span class="--spinner"></span>
    </span>

    <div class="checkout__address-autocomplete_result" v-if="!isLoading">
      <template v-if="($parent['inputAddress']['street'][1] || false) && $parent['inputAddress']['city']">
        <label v-if="resolveManually" @click="resolveAddress()">{{ $t('Click here to automatically retrieve your address') }}</label>
        <div v-if="!hasResolveError && resolvedAddress.hasOwnProperty('street')" class="resolved__address">
          <span>{{ $t('We have found the following address') }}:</span>
          <address>
            {{ resolvedAddress['street'] }} {{ resolvedAddress['house_nr'] }} {{ autoComplete['houseNrAdd'] }} <br />
            {{ resolvedAddress['zipcode'] }}, {{ resolvedAddress['place'] }}
          </address>
        </div>
      </template>
      <div v-if="!resolveManually && hasResolveError" class="resolved__address">
        <span class="_error">{{ $t('Invalid address. Are you sure it is correct?') }}</span>
        <label @click="resolveManually = true">{{ $t('Click here to enter the address manually.') }}</label>
      </div>
    </div>
    <div v-show="resolveManually">
      <div class="field__group">
        <ValidationProvider class="checkout__field street_0" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('Street')">
          <label>{{ $t('Street') }} *</label>
          <input type="text" :placeholder="$t('Street')" v-model="$parent['inputAddress']['street'][0]" :name="`${type}-address[street][0]`" :class="classes" />
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>
      <div class="field__group">
        <ValidationProvider class="checkout__field city" v-slot="{ errors, classes }" rules="required" :skip-if-empty="false" tag="div" :name="$t('City')">
          <label>{{ $t('City') }} *</label>
          <input type="text" :placeholder="$t('City')" v-model="$parent['inputAddress']['city']" :name="`${type}-address[city]`" :class="classes" />
          <span class="field-error" v-if="errors.length">{{ $t(errors[0]) }}</span>
        </ValidationProvider>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import {extend, ValidationProvider} from 'vee-validate';
  import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
  import {integer, max, min, regex, required} from 'vee-validate/dist/rules';
  import {appLocalStorage} from '../../../../../stores/checkout/services';

  extend('required', required);
  extend('integer', integer);
  extend('min', min);
  extend('max', max);
  extend('regex', regex);

  @Component({
    name: 'checkout-address-autocomplete',
    components: {
      ValidationProvider,
    },
  })
  export default class extends Vue {
    @Prop() readonly type: string;

    protected autoComplete: object = {
      postcode: null,
      houseNr: null,
      houseNrAdd: null,
    };

    private isLoading: boolean = false;
    private hasResolveError: boolean = false;
    private resolvedAddress: object = {};
    private resolveManually: boolean = false;
    private availableHouseNrAdditions: string[] = [];

    private async postcodeLookup(): Promise<void> {
      if (!this.autoComplete['postcode'] || !this.autoComplete['houseNr']) {
        return;
      }

      this.isLoading = true;

      try {
        const {data} = await this.$solarClient.get(`/api/postcode/lookup/${this.autoComplete['postcode']}/${this.autoComplete['houseNr']}`);

        this.resolvedAddress = data;
        this.availableHouseNrAdditions = this.resolvedAddress['houseNumberAdditions'] || [];

        if (this.availableHouseNrAdditions.length && !this.availableHouseNrAdditions.includes(this.autoComplete['houseNrAdd'])) {
          this.autoComplete['houseNrAdd'] = this.availableHouseNrAdditions[0] !== ''
            ? this.availableHouseNrAdditions[0]
            : null;
        }

        this.$parent['inputAddress']['street'] = [
          this.resolvedAddress['street'],
          this.resolvedAddress['house_nr'],
          this.autoComplete['houseNrAdd'] || this.resolvedAddress['houseNumberAddition'] || '',
        ];
        this.$parent['inputAddress']['postcode'] = this.resolvedAddress['zipcode'];
        this.$parent['inputAddress']['city'] = this.resolvedAddress['place'];

        this.hasResolveError = false;
        this.resolveManually = false;
      } catch (e) {
        if (e?.response?.status === 400) {
          this.hasResolveError = true;
          this.$parent['inputAddress']['street'] = ['', this.autoComplete['houseNr'], this.autoComplete['houseNrAdd'] || ''];
          this.$parent['inputAddress']['postcode'] = this.autoComplete['postcode'];
          this.$parent['inputAddress']['city'] = '';
        } else {
          console.error(e);
        }
      }

      this.isLoading = false;
    }

    private async changeHouseNrAddition(): Promise<void> {
      this.$parent['inputAddress']['street'][2] = this.autoComplete['houseNrAdd'];
    }

    /** Leaving manual mode and retrying when postcode / house number change. */
    private onLookupFieldsChange(): void {
      this.resolveManually = false;
      this.hasResolveError = false;
      this.resolvedAddress = {};
      this.postcodeLookup();
    }

    private resolveAddress(): void {
      this.resolveManually = false;
      this.hasResolveError = false;
      this.postcodeLookup();
    }

    private created(): void {
      const saved = appLocalStorage.getItem(`checkout-autocomplete-${this.type}-address`, null);
      if (saved) {
        this.autoComplete = saved;
      }

      if (null !== this.autoComplete['postcode'] && null !== this.autoComplete['houseNr']) {
        this.postcodeLookup();
      }
    }

    @Watch('autoComplete', {deep: true})
    handle(nv): void {
      appLocalStorage.setItem(`checkout-autocomplete-${this.type}-address`, nv);
    }
  }
</script>
